import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Card, CardContent } from "@/modules/shared/ui/components/card"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { SearchFilterBar } from "@/modules/shared/ui/components/custom/SearchFilterBar"
import { OffboardingCard } from "@/modules/offboarding/ui/components/OffboardingCard"
import {
  fetchOffboardingsThunk,
  selectOffboardings,
  selectOffboardingLoading,
  selectOffboardingError,
} from "@/modules/offboarding/store/offboardingSlice"
import type { AppDispatch } from "@/store"
import { OffboardingStatus } from "@/modules/offboarding/enums"

const STATUSES = [
  { label: "All Statuses",  value: "all" },
  { label: "Pending",       value: OffboardingStatus.Pending },
  { label: "Accepted",      value: OffboardingStatus.Accepted },
  { label: "Rejected",      value: OffboardingStatus.Rejected },
]

export default function OffboardingPage() {
  const dispatch = useDispatch<AppDispatch>()
  const offboardings = useSelector(selectOffboardings)
  const isLoading = useSelector(selectOffboardingLoading)
  const error = useSelector(selectOffboardingError)
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")

  useEffect(() => {
    const term = searchQuery.trim() || undefined
    const resignationStatus = statusFilter === "all" ? undefined : statusFilter
    dispatch(fetchOffboardingsThunk({ searchTerm: term, resignationStatus }))
  }, [searchQuery, statusFilter, dispatch])

  return (
    <div className="space-y-6 p-6">
      <Card className="bg-card border border-border">
        <CardContent className="p-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Offboarding</h1>
            <p className="text-muted-foreground mt-1">
              Track and manage employee offboarding processes in your organisation
            </p>
          </div>

          <SearchFilterBar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search by name, email or position..."
            filterValue={statusFilter}
            onFilterChange={setStatusFilter}
            filterOptions={STATUSES}
            filterPlaceholder="All Statuses"
            hasData={offboardings.length > 0}
          />
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner className="size-6" />
        </div>
      ) : error ? (
        <Card className="bg-card border border-border">
          <CardContent className="p-12 text-center">
            <p className="text-red-500 font-medium">Failed to load offboardings</p>
            <p className="text-muted-foreground text-sm mt-1">{error}</p>
          </CardContent>
        </Card>
      ) : offboardings.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {offboardings.map((offboarding) => (
            <OffboardingCard key={offboarding.id} offboarding={offboarding} />
          ))}
        </div>
      ) : (
        <Card className="bg-card border border-border">
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground font-medium">No offboarding records found</p>
            <p className="text-muted-foreground text-sm mt-1">
              Offboarding records will appear here once initiated
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
